/*
 * Snibbs T11.5 — shared PDP buy-box engine (external asset).
 * Ported from the T11 inline state controller (main-product-t18.liquid) and generalized:
 *  - family membership comes from rendered data (data-sn-t115-family / data-sn-t115-inline on tiles), never a handle prefix;
 *  - the Section Rendering id comes from the current root (data-section), never a literal;
 *  - destination validation also requires family and section-id equality;
 *  - one install per document (window.__snT115Engine), explicit boot(root) after every swap.
 * Emits DOM CustomEvents: sn:t115:color-click, sn:t115:color-switch-success|fallback, sn:t115:color-restore-success|fallback.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = 'product-info[data-sn-t115-native]';
  var GRID_SELECTOR = '[data-sn-t115-color-grid]';
  var GRID_LINK = '[data-sn-t115-color-grid] a[href]';
  var PREVIEW_PARAMS = ['view', '_ab', '_fd', '_sc'];

  if (window.__snT115Engine) {
    window.__snT115Engine.boot(document.querySelector(ROOT_SELECTOR));
    return;
  }

  var root = null;
  var state = { width: null, size: null };
  var scheduled = false;
  var previewParams = {};
  var observer = null;
  var requestController = null;
  var requestToken = 0;
  var cache = new Map();
  var preloads = new Map();
  var preloadStarted = false;
  var lastScrollInput = 0;
  var engine = {
    version: 't115.5',
    installs: 1,
    boots: 0,
    clicks: 0,
    successes: 0,
    fallbacks: 0,
    preloaded: 0,
    preloadFailures: 0,
    boot: boot
  };

  window.__snT115Engine = engine;

  PREVIEW_PARAMS.forEach(function (name) {
    var value = new URL(window.location.href).searchParams.get(name);
    if (value !== null) previewParams[name] = value;
  });

  function family() {
    return (root && root.dataset.snT115Family) || '';
  }

  function inputs(name) {
    return Array.prototype.slice.call(root.querySelectorAll('.pill-' + name + ' input[type="radio"]'));
  }

  function setChoice(name, value) {
    inputs(name).forEach(function (input) {
      var checked = value !== null && input.value === value;
      if (input.checked !== checked) input.checked = checked;
      if (!checked && input.hasAttribute('checked')) input.removeAttribute('checked');
    });
  }

  function missingChoice() {
    if (inputs('Width').length && !state.width) return true;
    return inputs('Size').length && !state.size;
  }

  function preserveColorwayPreview() {
    if (!root) return;
    root.querySelectorAll(GRID_LINK).forEach(function (link) {
      var url = new URL(link.href, window.location.origin);
      Object.keys(previewParams).forEach(function (name) {
        url.searchParams.set(name, previewParams[name]);
      });
      link.href = url.toString();
    });
  }

  function preservePagePreview() {
    var names = Object.keys(previewParams);
    if (!names.length) return;
    var url = new URL(window.location.href);
    var changed = false;
    names.forEach(function (name) {
      if (url.searchParams.get(name) === previewParams[name]) return;
      url.searchParams.set(name, previewParams[name]);
      changed = true;
    });
    if (changed) history.replaceState(history.state, '', url.href);
  }

  function pageKey(url) {
    var normalized = new URL(url, window.location.origin);
    normalized.hash = '';
    normalized.searchParams.delete('section_id');
    normalized.searchParams.delete('variant');
    normalized.searchParams.delete('option_values');
    // Shopify removes this parameter after setting its preview cookie.
    normalized.searchParams.delete('preview_theme_id');
    normalized.searchParams.sort();
    return normalized.href;
  }

  function productTitle(scope) {
    var activeColor = scope && scope.querySelector('[data-sn-t115-color-grid] a[aria-current="page"]');
    if (activeColor && activeColor.dataset.productName) return activeColor.dataset.productName.trim();
    var title = scope && scope.querySelector('.product__title h1, .product__title');
    return title ? title.textContent.trim() : '';
  }

  function emit(name, detail) {
    detail = detail || {};
    detail.family = detail.family || family();
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    if (root && root.dataset.snT115IgBridge === '1') {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: name.replace(/[^a-z0-9]+/gi, '_'), family: detail.family });
    }
  }

  function clearBusy(section) {
    if (!section || !section.isConnected) return;
    section.removeAttribute('aria-busy');
    section.querySelectorAll('product-form, [data-sn-t115-payment-terms]').forEach(function (control) {
      control.removeAttribute('inert');
    });
  }

  function setBusy(section) {
    if (!section || !section.isConnected) return;
    section.setAttribute('aria-busy', 'true');
    section.querySelectorAll('product-form, [data-sn-t115-payment-terms]').forEach(function (control) {
      control.setAttribute('inert', '');
    });
  }

  function imageSource(image) {
    if (!image) return '';
    return image.currentSrc || image.getAttribute('src') || image.getAttribute('data-src') || '';
  }

  function decodeImage(source) {
    if (!source) return Promise.resolve();
    return new Promise(function (resolve) {
      var image = new Image();
      var settled = false;
      var timeout = window.setTimeout(done, 4000);
      function done() {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve();
      }
      image.decoding = 'async';
      image.onload = function () {
        if (typeof image.decode !== 'function') {
          done();
          return;
        }
        image.decode().then(done).catch(done);
      };
      image.onerror = done;
      image.src = source;
      if (image.complete) image.onload();
    });
  }

  function waitForPrimaryMedia(section) {
    return decodeImage(imageSource(section.querySelector('media-gallery img, .product__media img, img')));
  }

  function reservePaymentTermsSpace(currentSection, nextSection) {
    var currentTerms = currentSection.querySelector('[data-sn-t115-payment-terms]');
    var nextTerms = nextSection.querySelector('[data-sn-t115-payment-terms]');
    if (!currentTerms || !nextTerms) return { height: 0, marginBottom: null };
    var height = Math.ceil(currentTerms.getBoundingClientRect().height);
    if (height > 0) nextTerms.style.minHeight = height + 'px';
    var currentFollowing = currentSection.querySelector('[data-sn-t115-value-props]');
    var marginBottom = null;
    if (currentFollowing && height > 0) {
      var desiredOffset = currentFollowing.getBoundingClientRect().top - currentTerms.getBoundingClientRect().top;
      var followingMarginTop = parseFloat(window.getComputedStyle(currentFollowing).marginTop) || 0;
      marginBottom = desiredOffset - height - followingMarginTop;
    }
    return { height: height, marginBottom: marginBottom };
  }

  function lockPaymentTerms(container, metrics) {
    if (!container || metrics.height <= 0) return null;
    var form = container.querySelector('form');
    var terms = container.querySelector('shopify-payment-terms');
    container.style.height = metrics.height + 'px';
    container.style.minHeight = metrics.height + 'px';
    container.style.overflow = 'hidden';
    if (Number.isFinite(metrics.marginBottom)) container.style.marginBottom = metrics.marginBottom + 'px';
    if (form) form.style.setProperty('margin', '0', 'important');
    return { container: container, form: form, terms: terms };
  }

  function releasePaymentTermsWhenReady(lock) {
    if (!lock) return;
    var frames = 0;
    var sawEmpty = !lock.terms || lock.terms.getBoundingClientRect().height <= 0;
    var released = false;
    function release() {
      if (released) return;
      released = true;
      lock.container.style.removeProperty('height');
      lock.container.style.removeProperty('min-height');
      lock.container.style.removeProperty('overflow');
      lock.container.style.removeProperty('margin-bottom');
      if (lock.form) lock.form.style.removeProperty('margin');
    }
    function check() {
      frames += 1;
      if (!lock.container.isConnected) {
        if (frames < 120) window.requestAnimationFrame(check);
        else release();
        return;
      }
      var termsHeight = lock.terms ? lock.terms.getBoundingClientRect().height : 1;
      if (termsHeight <= 0) sawEmpty = true;
      if ((sawEmpty && termsHeight > 0) || (!sawEmpty && frames >= 3) || frames >= 120) {
        window.requestAnimationFrame(release);
        return;
      }
      window.requestAnimationFrame(check);
    }
    window.requestAnimationFrame(check);
  }

  function paymentTermsOffer(section) {
    var terms = section.querySelector('[data-sn-t115-payment-terms] shopify-payment-terms');
    if (terms) {
      try {
        var metadata = JSON.parse(terms.getAttribute('shopify-meta') || '{}');
        var variantId = terms.getAttribute('variant-id');
        var variants = metadata.variants || [];
        var variant = variants.filter(function (item) { return String(item.id) === String(variantId); })[0] || variants[0];
        if (variant && variant.full_price) {
          return [metadata.currency_code || '', metadata.country_code || '', variant.full_price].join('|');
        }
      } catch (error) {}
    }
    var price = section.querySelector('.product-price');
    return price ? price.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function syncAttributes(currentElement, nextElement) {
    Array.prototype.slice.call(currentElement.attributes).forEach(function (attribute) {
      if (!nextElement.hasAttribute(attribute.name)) currentElement.removeAttribute(attribute.name);
    });
    Array.prototype.forEach.call(nextElement.attributes, function (attribute) {
      currentElement.setAttribute(attribute.name, attribute.value);
    });
  }

  function childOnPath(parent, descendant) {
    var child = descendant;
    while (child && child.parentElement !== parent) child = child.parentElement;
    return child && child.parentElement === parent ? child : null;
  }

  function replaceChildrenAround(currentParent, currentKept, nextParent, nextKept) {
    Array.prototype.slice.call(currentParent.childNodes).forEach(function (child) {
      if (child !== currentKept) child.remove();
    });
    var nextChildren = Array.prototype.slice.call(nextParent.childNodes);
    var keptIndex = nextChildren.indexOf(nextKept);
    if (keptIndex < 0) return false;
    nextChildren.forEach(function (child, index) {
      if (child === nextKept) return;
      if (index < keptIndex) currentParent.insertBefore(child, currentKept);
      else currentParent.appendChild(child);
    });
    return true;
  }

  function patchPreservingNode(currentElement, nextElement, currentPreserved, nextPreserved) {
    if (currentElement === currentPreserved) return true;
    var currentChild = childOnPath(currentElement, currentPreserved);
    var nextChild = childOnPath(nextElement, nextPreserved);
    if (!currentChild || !nextChild) return false;
    syncAttributes(currentElement, nextElement);
    if (!replaceChildrenAround(currentElement, currentChild, nextElement, nextChild)) return false;
    return patchPreservingNode(currentChild, nextChild, currentPreserved, nextPreserved);
  }

  // Generic hygiene for an imported section: never carry a foreign engine tag or stale
  // client-side injection artifacts (e.g. an ended video-hero test) into the live document.
  function sanitizeImportedSection(section) {
    section.querySelectorAll('script[data-sn-t115-engine]').forEach(function (node) { node.remove(); });
    section.querySelectorAll('.sn-video-hero, .sn-video-hero-modal').forEach(function (node) { node.remove(); });
    section.querySelectorAll('.sn-video-hero-active').forEach(function (node) { node.classList.remove('sn-video-hero-active'); });
  }

  function preservePaymentTerms(currentSection, nextSection) {
    var currentTerms = currentSection.querySelector('[data-sn-t115-payment-terms]');
    var nextTerms = nextSection.querySelector('[data-sn-t115-payment-terms]');
    if (!currentTerms || !nextTerms) return { preserved: false, lock: null };
    var currentOffer = paymentTermsOffer(currentSection);
    var nextOffer = paymentTermsOffer(nextSection);
    if (currentOffer && currentOffer === nextOffer) {
      if (patchPreservingNode(currentSection, nextSection, currentTerms, nextTerms)) {
        return { preserved: true, lock: null };
      }
    }
    var metrics = reservePaymentTermsSpace(currentSection, nextSection);
    return { preserved: false, lock: lockPaymentTerms(nextTerms, metrics) };
  }

  function requestSection(destination, sectionId, signal) {
    var url = new URL(destination.href);
    url.searchParams.set('section_id', sectionId);
    return fetch(url.href, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      signal: signal
    }).then(function (response) {
      if (!response.ok) throw new Error('section request failed: ' + response.status);
      return response.text();
    });
  }

  function preloadColors(scope) {
    if (preloadStarted || !scope || !scope.isConnected) return;
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection && (connection.saveData || /(^|-)2g/.test(connection.effectiveType || ''))) return;
    var section = scope.closest('.shopify-section');
    if (!section) return;
    preloadStarted = true;
    // Fetch the original color too. Never cache the live DOM: native/app
    // initialization has already mutated its form, scripts and embedded widgets.
    var urls = Array.prototype.map.call(
      scope.querySelectorAll('[data-sn-t115-color-grid] a[data-sn-t115-inline="1"]'),
      function (link) { return new URL(link.href); }
    ).filter(function (url) { return url.origin === window.location.origin; });
    var sectionId = scope.dataset.section;
    function next() {
      var destination = urls.shift();
      if (!destination) return;
      var key = pageKey(destination);
      if (cache.has(key) || preloads.has(key)) { next(); return; }
      var controller = new AbortController();
      var timeout = window.setTimeout(function () { controller.abort(); }, 8000);
      var pending = requestSection(destination, sectionId, controller.signal).then(function (html) {
        var parsed = validateSection(html, destination, section, sectionId);
        return waitForPrimaryMedia(parsed).then(function () {
          cache.set(key, html);
          engine.preloaded += 1;
          return html;
        });
      });
      preloads.set(key, pending);
      // Passive failures never navigate. A later tap retries through the normal path.
      pending.catch(function () { engine.preloadFailures += 1; }).finally(function () {
        window.clearTimeout(timeout);
        preloads.delete(key);
        next();
      });
    }
    // Start on initial boot, with two workers rather than an unbounded catalog burst.
    next();
    next();
  }

  function fallBack(url, section, error, historyMode) {
    if (historyMode === 'push') rememberAnchor(url.href);
    clearBusy(section);
    engine.fallbacks += 1;
    emit(historyMode === 'push' ? 'sn:t115:color-switch-fallback' : 'sn:t115:color-restore-fallback', {
      destination: url.href,
      source: window.location.href,
      reason: error && error.message ? error.message : 'unknown'
    });
    window.location.assign(url.href);
  }

  function validateSection(html, destination, currentSection, sectionId) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var expectedId = currentSection.id || ('shopify-section-' + sectionId);
    var sections = Array.prototype.filter.call(parsed.querySelectorAll('.shopify-section'), function (section) {
      return section.id === expectedId;
    });
    if (sections.length !== 1) throw new Error('invalid section wrapper');

    var nextSection = sections[0];
    var roots = nextSection.querySelectorAll(ROOT_SELECTOR);
    if (roots.length !== 1) throw new Error('invalid product root');

    var nextRoot = roots[0];
    if (nextRoot.dataset.section !== sectionId) throw new Error('destination section mismatch');
    var currentFamily = family();
    if (!currentFamily || nextRoot.dataset.snT115Family !== currentFamily) throw new Error('destination family mismatch');

    var renderedPath = new URL(nextRoot.dataset.url || '', window.location.origin).pathname;
    if (renderedPath !== destination.pathname) throw new Error('destination path mismatch');

    var variant = nextRoot.querySelector('product-form form[action*="/cart/add"] [name="id"], product-form [name="id"]');
    if (!variant || !/^\d+$/.test(variant.value)) throw new Error('missing destination variant');

    var colors = nextRoot.querySelectorAll('[data-sn-t115-color-grid] > li');
    var colorLinks = nextRoot.querySelectorAll('[data-sn-t115-color-grid] > li > a[href]');
    var activeColors = nextRoot.querySelectorAll('[data-sn-t115-color-grid] .active-prd');
    var accessibleColors = nextRoot.querySelectorAll('[data-sn-t115-color-grid] a[aria-current="page"]');
    var foreign = Array.prototype.filter.call(colorLinks, function (link) {
      return link.dataset.snT115Family !== currentFamily;
    });
    var activePath = accessibleColors.length === 1
      ? new URL(accessibleColors[0].href, window.location.origin).pathname
      : '';
    if (
      colors.length < 1 ||
      colorLinks.length !== colors.length ||
      activeColors.length !== 1 ||
      accessibleColors.length !== 1 ||
      foreign.length !== 0 ||
      activePath !== destination.pathname
    ) {
      throw new Error('invalid destination color grid');
    }
    return nextSection;
  }

  function updateDocumentMetadata(nextRoot, destination) {
    var nextTitle = productTitle(nextRoot);
    if (nextTitle) {
      var brandSuffix = document.title.match(/\s[-–—]\sSnibbs$/);
      document.title = nextTitle + (brandSuffix ? brandSuffix[0] : ' - Snibbs');
    }
    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = destination.origin + destination.pathname;
  }

  function scrollInstantly(callback) {
    var elements = [document.documentElement, document.body].filter(Boolean);
    var states = elements.map(function (element) {
      return {
        element: element,
        value: element.style.getPropertyValue('scroll-behavior'),
        priority: element.style.getPropertyPriority('scroll-behavior')
      };
    });
    elements.forEach(function (element) {
      element.style.setProperty('scroll-behavior', 'auto', 'important');
    });
    try {
      callback();
    } finally {
      states.forEach(function (state) {
        if (state.value) state.element.style.setProperty('scroll-behavior', state.value, state.priority);
        else state.element.style.removeProperty('scroll-behavior');
      });
    }
  }

  function gridRect(scope) {
    var grid = scope && scope.querySelector(GRID_SELECTOR);
    if (!grid) return null;
    var rect = grid.getBoundingClientRect();
    return rect.height > 0 ? rect : null;
  }

  // Hold the color tiles where the shopper tapped them, not just the scroll offset.
  // A colorway with a different photo count or photo shape changes the gallery height,
  // which moves the sticky buy box (desktop) or everything under the gallery (phone)
  // while scrollY stays the same. Off-screen tiles (e.g. back/forward while reading
  // reviews) fall back to restoring the scroll offset.
  function captureView(scope) {
    var rect = gridRect(scope);
    var visible = rect && rect.bottom > 0 && rect.top < window.innerHeight;
    return {
      position: { x: window.scrollX, y: window.scrollY },
      anchorTop: visible ? rect.top : null,
      startedAt: performance.now()
    };
  }

  function viewDrift(view) {
    if (view.anchorTop === null) return window.scrollY - view.position.y;
    var rect = gridRect(root);
    return rect ? rect.top - view.anchorTop : 0;
  }

  // One correction step. Returns false when scrolling cannot reduce the drift (page edge,
  // or a pinned sticky column); the scroll is then left where it was.
  function alignView(view) {
    var drift = viewDrift(view);
    if (Math.abs(drift) <= 1) return true;
    var previousScrollY = window.scrollY;
    scrollInstantly(function () {
      if (view.anchorTop === null) window.scrollTo(view.position.x, view.position.y);
      else window.scrollTo(window.scrollX, previousScrollY + drift);
    });
    if (window.scrollY === previousScrollY) return false;
    if (view.anchorTop !== null && Math.abs(viewDrift(view)) > Math.abs(drift) - 1) {
      scrollInstantly(function () { window.scrollTo(window.scrollX, previousScrollY); });
      return false;
    }
    return true;
  }

  // A swapped-in section re-inserts its stylesheet links; until they apply, the section lays
  // out unstyled (no sticky column, collapsed gallery) and any measurement is meaningless.
  function stylesApplied(scope) {
    var section = scope && scope.closest('.shopify-section');
    if (!section) return true;
    return Array.prototype.every.call(section.querySelectorAll('link[rel="stylesheet"]'), function (link) {
      return !!link.sheet;
    });
  }

  // ResizeObserver callbacks run after layout and before paint, so a correction made here
  // uses the final layout of that frame and is never painted in its uncorrected state.
  // (Measuring synchronously after the swap sees transient pre-render states instead.)
  var layoutWatch = null;

  function stopLayoutWatch() {
    if (layoutWatch) layoutWatch.disconnect();
    layoutWatch = null;
  }

  // untilLoaded: keep watching until shortly after the page's load event (a fresh page),
  // otherwise for a short window after an in-place swap.
  function watchLayout(scope, view, token, untilLoaded) {
    stopLayoutWatch();
    if (view.anchorTop === null || typeof ResizeObserver !== 'function') return;
    var watch = new ResizeObserver(function () {
      if (layoutWatch !== watch) return;
      if (token !== requestToken || lastScrollInput > view.startedAt || !root || !root.isConnected) {
        stopLayoutWatch();
        return;
      }
      if (stylesApplied(root)) alignView(view);
    });
    layoutWatch = watch;
    var section = scope.closest('.shopify-section');
    [section, scope.querySelector('media-gallery'), scope.querySelector('.product__info-container')].forEach(function (element) {
      if (element) watch.observe(element);
    });
    function stopSoon() {
      window.setTimeout(function () {
        if (layoutWatch === watch) stopLayoutWatch();
      }, 1500);
    }
    if (!untilLoaded || document.readyState === 'complete') {
      stopSoon();
    } else {
      window.addEventListener('load', stopSoon, { once: true });
      window.setTimeout(function () {
        if (layoutWatch === watch) stopLayoutWatch();
      }, 15000);
    }
  }

  var ANCHOR_KEY = 'snT115Anchor';

  function rememberAnchor(destination) {
    var rect = gridRect(root);
    if (!rect || rect.bottom <= 0 || rect.top >= window.innerHeight) return;
    try {
      window.sessionStorage.setItem(ANCHOR_KEY, JSON.stringify({
        path: new URL(destination, window.location.origin).pathname,
        top: rect.top,
        at: Date.now()
      }));
    } catch (error) {}
  }

  // A color on another template (or a fallback) loads as a full page. Open that page with the
  // tiles where the shopper tapped them, rather than wherever the browser happens to land.
  function restoreAnchor(scope) {
    var saved = null;
    try {
      saved = JSON.parse(window.sessionStorage.getItem(ANCHOR_KEY) || 'null');
      window.sessionStorage.removeItem(ANCHOR_KEY);
    } catch (error) {}
    if (!saved || saved.path !== window.location.pathname || !(Date.now() - saved.at < 15000)) return;
    // A fresh page keeps settling until it loads (installment terms and app widgets render
    // late, seconds after first paint in Safari), so watch until shortly after load.
    var view = { position: { x: window.scrollX, y: window.scrollY }, anchorTop: saved.top, startedAt: performance.now() };
    var token = requestToken;
    watchLayout(scope, view, token, true);
    window.requestAnimationFrame(function () {
      settleScrollPosition(view, 60, function () {}, 0, token);
    });
  }

  function settleScrollPosition(view, attempts, done, stableFrames, token) {
    if (token !== requestToken) return;
    // Never fight the shopper: their own scroll ends the correction.
    if (!view || !root || !root.isConnected || lastScrollInput > view.startedAt) {
      done();
      return;
    }
    var stable = stableFrames || 0;
    if (!stylesApplied(root)) {
      stable = 0;
    } else if (Math.abs(viewDrift(view)) <= 1) {
      stable += 1;
    } else {
      stable = 0;
      if (!alignView(view)) {
        done();
        return;
      }
    }
    if (stable >= 3 || attempts <= 0) {
      done();
      return;
    }
    window.requestAnimationFrame(function () {
      settleScrollPosition(view, attempts - 1, done, stable, token);
    });
  }

  function installSection(nextSection, destination, currentSection, historyMode, token) {
    // Captured at install time, so scrolling while the color loaded is respected.
    var view = captureView(root);
    var previousRoot = root;
    var previousUrl = previousRoot && previousRoot.dataset.url;
    var importedSection = document.importNode(nextSection, true);
    sanitizeImportedSection(importedSection);
    var paymentTermsState = preservePaymentTerms(currentSection, importedSection);
    var installedSection = currentSection;
    if (!paymentTermsState.preserved) {
      currentSection.replaceWith(importedSection);
      installedSection = importedSection;
    }
    setBusy(installedSection);
    var nextRoot = installedSection.querySelector(ROOT_SELECTOR);
    boot(nextRoot);
    if (!paymentTermsState.preserved && window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
      window.Shopify.PaymentButton.init();
    }
    releasePaymentTermsWhenReady(paymentTermsState.lock);
    updateDocumentMetadata(nextRoot, destination);

    if (historyMode === 'push') history.pushState({ snT115Color: true }, '', destination.href);

    scrollInstantly(function () { window.scrollTo(view.position.x, view.position.y); });
    watchLayout(nextRoot, view, token);
    var focusAttempts = 0;
    function finishInstall() {
      if (token !== requestToken || !nextRoot.isConnected || root !== nextRoot) return;
      var activeColor = nextRoot.querySelector('[data-sn-t115-color-grid] a[aria-current="page"]');
      if (activeColor) {
        try {
          activeColor.focus({ preventScroll: true });
        } catch (error) {
          activeColor.focus();
        }
      }
      settleScrollPosition(view, 60, function () {
        if (activeColor && document.activeElement !== activeColor && focusAttempts < 1) {
          focusAttempts += 1;
          window.requestAnimationFrame(finishInstall);
          return;
        }
        if (token !== requestToken) return;
        clearBusy(installedSection);
        engine.successes += 1;
        emit(historyMode === 'push' ? 'sn:t115:color-switch-success' : 'sn:t115:color-restore-success', {
          destination: destination.href,
          source: previousUrl,
          productId: nextRoot.dataset.productId,
          historyMode: historyMode
        });

      }, 0, token);
    }
    window.requestAnimationFrame(finishInstall);
  }

  function loadColor(destinationUrl, historyMode) {
    var destination = new URL(destinationUrl, window.location.origin);
    if (!root || !root.isConnected) {
      fallBack(destination, null, new Error('missing current product root'), historyMode);
      return;
    }
    var currentSection = root.closest('.shopify-section');
    var sectionId = root.dataset.section;
    if (!currentSection || !sectionId) {
      fallBack(destination, currentSection, new Error('missing current section'), historyMode);
      return;
    }

    var token = ++requestToken;
    if (requestController) requestController.abort();
    requestController = new AbortController();
    setBusy(currentSection);

    var key = pageKey(destination);
    var cached = cache.get(key);
    var sectionPromise;
    if (cached) {
      sectionPromise = Promise.resolve(cached);
    } else if (preloads.has(key)) {
      sectionPromise = preloads.get(key).catch(function () {
        if (token !== requestToken) throw new DOMException('Superseded color', 'AbortError');
        return requestSection(destination, sectionId, requestController.signal);
      });
    } else {
      sectionPromise = requestSection(destination, sectionId, requestController.signal);
    }

    sectionPromise.then(function (html) {
      if (token !== requestToken) return;
      var nextSection = validateSection(html, destination, currentSection, sectionId);
      cache.set(key, html);
      return waitForPrimaryMedia(nextSection).then(function () {
        if (token !== requestToken) return;
        installSection(nextSection, destination, currentSection, historyMode, token);
      });
    }).catch(function (error) {
      if (token !== requestToken || error.name === 'AbortError') return;
      fallBack(destination, currentSection, error, historyMode);
    });
  }

  function isPrimaryUnmodifiedClick(event, link) {
    if (event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target.toLowerCase() !== '_self') return false;
    return true;
  }

  // Inline only when the rendered tile says so: same family as the current root, marked inline,
  // same origin, and the href really is that tile's own product path.
  function shouldSwitchInline(event, link) {
    if (!isPrimaryUnmodifiedClick(event, link)) return false;
    if (link.closest('.active-prd') || link.getAttribute('aria-current') === 'page') return false;
    var currentFamily = family();
    if (!currentFamily || link.dataset.snT115Family !== currentFamily) return false;
    if (link.dataset.snT115Inline !== '1') return false;
    var url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return false;
    var handle = link.dataset.snT115Handle || '';
    return handle !== '' && url.pathname === '/products/' + handle;
  }

  function updateCta() {
    var button = root.querySelector('product-form button[type="submit"], product-form [data-atc]');
    var text = button && button.querySelector('[data-atc-text], span');
    if (!button || !text) return;
    if (missingChoice()) {
      if (!/^Select (width|size)$/i.test(text.textContent.trim())) {
        button.setAttribute('data-sn-t115-native-label', text.textContent.trim());
      }
      if (text.textContent !== 'Select Size') text.textContent = 'Select Size';
      button.setAttribute('disabled', '');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.setAttribute('aria-disabled', 'false');
      if (/^Select (width|size)$/i.test(text.textContent.trim())) {
        text.textContent = button.getAttribute('data-sn-t115-native-label') || 'Add to cart';
      }
    }
  }

  function bind() {
    if (!root || !observer) return;
    observer.disconnect();
    preserveColorwayPreview();
    preservePagePreview();
    setChoice('Width', state.width);
    setChoice('Size', state.size);
    // Native ProductForm caches the document's upsell checkbox in its constructor.
    // Imported sections upgrade while the previous section still exists, so bind
    // the form to this product's live checkbox after every section/variant redraw.
    root.querySelectorAll('product-form').forEach(function (form) {
      if (!('upsellCheckbox' in form)) return;
      form.upsellCheckbox = root.querySelector('[name="upsell"]');
      form.upsellProductId = form.upsellCheckbox && form.upsellCheckbox.getAttribute('data-upsell-product-id');
    });
    updateCta();
    observer.observe(root, { childList: true, subtree: true });
  }

  function scheduleBind() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(function () {
      scheduled = false;
      bind();
    }, 0);
  }

  function boot(nextRoot) {
    if (!nextRoot) return false;
    if (observer) observer.disconnect();
    root = nextRoot;
    // Native section swaps already preserve scroll; browser anchoring fights that
    // while payment terms and gallery elements reconnect.
    document.documentElement.classList.add('sn-t115-page');
    state = {
      width: inputs('Width').some(function (input) { return input.value === 'Standard'; }) ? 'Standard' : null,
      size: null
    };
    scheduled = false;
    observer = new MutationObserver(scheduleBind);
    bind();
    if (engine.boots === 0) restoreAnchor(nextRoot);
    engine.boots += 1;
    window.setTimeout(function () { preloadColors(nextRoot); }, 0);
    return true;
  }

  document.addEventListener('change', function (event) {
    if (!root || !root.contains(event.target)) return;
    if (event.target.matches('.pill-Width input[type="radio"]')) state.width = event.target.value;
    if (event.target.matches('.pill-Size input[type="radio"]')) state.size = event.target.value;
    scheduleBind();
  }, true);

  document.addEventListener('click', function (event) {
    var colorLink = event.target.closest(GRID_LINK);
    if (!colorLink || !root || !root.contains(colorLink)) return;
    if (!isPrimaryUnmodifiedClick(event, colorLink)) return;
    if (colorLink.closest('.active-prd') || colorLink.getAttribute('aria-current') === 'page') {
      event.preventDefault();
      // A tap back to the displayed color cancels an in-flight change, without a reload.
      requestToken += 1;
      if (requestController) requestController.abort();
      clearBusy(root.closest('.shopify-section'));
      return;
    }
    var inline = shouldSwitchInline(event, colorLink);
    engine.clicks += 1;
    emit('sn:t115:color-click', {
      source: window.location.href,
      destination: colorLink.href,
      handle: colorLink.dataset.snT115Handle || '',
      inline: inline
    });
    if (inline) {
      event.preventDefault();
      loadColor(colorLink.href, 'push');
    } else {
      rememberAnchor(colorLink.href);
    }
  }, true);

  document.addEventListener('submit', function (event) {
    if (root && root.contains(event.target) && missingChoice()) event.preventDefault();
  }, true);

  ['wheel', 'touchmove', 'keydown'].forEach(function (type) {
    window.addEventListener(type, function () { lastScrollInput = performance.now(); }, { capture: true, passive: true });
  });

  window.addEventListener('popstate', function () {
    if (!root || !root.isConnected) return;
    loadColor(window.location.href, 'pop');
  });

  boot(document.querySelector(ROOT_SELECTOR));
})();
